/* =====================================================================
   Simulador de Financiamento — utilitários puros (formatação, máscaras,
   validações e matemática de financiamento). Portado do protótipo
   `simulador-financiamento/script.js`.
   ===================================================================== */

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

export const fmtBRL = (n: number): string => brl.format(Math.round(n || 0));
export const fmtNum = (n: number): string => new Intl.NumberFormat('pt-BR').format(n || 0);
export const fmtPct = (n: number, d: number): string => (n || 0).toFixed(d).replace('.', ',');

export const onlyDigits = (v: string): string => (v || '').replace(/\D/g, '');

export const maskPhone = (v: string): string => {
  const d = onlyDigits(v).slice(0, 11);
  if (d.length <= 2) return d.replace(/(\d{0,2})/, '($1');
  if (d.length <= 6) return d.replace(/(\d{2})(\d{0,4})/, '($1) $2');
  if (d.length <= 10) return d.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
  return d.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
};

export const maskCPF = (v: string): string => {
  const d = onlyDigits(v).slice(0, 11);
  return d
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

/** máscara de milhar (quilometragem / dinheiro sem centavos). */
export const maskThousand = (v: string): string => {
  const d = onlyDigits(v);
  return d ? fmtNum(parseInt(d, 10)) : '';
};

export const isValidEmail = (v: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((v || '').trim());

export const isValidName = (v: string): boolean => {
  const t = (v || '').trim();
  return t.length >= 5 && t.split(/\s+/).filter(Boolean).length >= 2;
};

export const isValidCPF = (v: string): boolean => {
  const c = onlyDigits(v);
  if (c.length !== 11 || /^(\d)\1{10}$/.test(c)) return false;
  let s = 0;
  for (let i = 0; i < 9; i++) s += parseInt(c[i], 10) * (10 - i);
  let d1 = (s * 10) % 11;
  if (d1 === 10) d1 = 0;
  if (d1 !== parseInt(c[9], 10)) return false;
  s = 0;
  for (let i = 0; i < 10; i++) s += parseInt(c[i], 10) * (11 - i);
  let d2 = (s * 10) % 11;
  if (d2 === 10) d2 = 0;
  return d2 === parseInt(c[10], 10);
};

export const ageFromDate = (iso: string): number | null => {
  if (!iso) return null;
  const b = new Date(iso + 'T00:00:00');
  if (isNaN(b.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - b.getFullYear();
  const m = today.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < b.getDate())) age--;
  return age;
};

/** CPF mascarado para exibição no resumo (mostra só os 2 últimos dígitos). */
export const maskCPFout = (cpf: string): string => {
  const d = onlyDigits(cpf);
  return d.length === 11 ? `***.***.***-${d.slice(9)}` : cpf;
};

/** Parcela da simulação genérica: divisão simples do valor financiado (sem juros). */
export const simpleInstallment = (financed: number, months: number): number =>
  financed > 0 && months > 0 ? financed / months : 0;

export const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
